import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, UploadCloud, FileText, Loader2, Download, Eye, X, Trash2, Printer, Send, Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

// When axios is configured with responseType: 'blob', error bodies arrive as
// Blob objects too — so the FastAPI `{"detail":"..."}` JSON has to be parsed
// out manually before we can surface it in a toast.
async function extractBlobError(err) {
  try {
    const body = err?.response?.data;
    if (body && typeof body.text === 'function') {
      const txt = await body.text();
      try {
        const parsed = JSON.parse(txt);
        if (parsed?.detail) return parsed.detail;
      } catch (_) { /* fall through */ }
      if (txt) return txt;
    }
    if (body?.detail) return body.detail;
  } catch (_) { /* ignore */ }
  if (err?.code === 'ECONNABORTED') {
    return 'Request timed out — try a smaller file or fewer pages at a time.';
  }
  return err?.message || 'Unknown error';
}

// Trigger a browser download for an already-rendered blob. Centralised so the
// page can call it both from the per-file convert flow and from the
// "Ready downloads" panel when the user re-clicks save on a previously
// converted file (no re-conversion roundtrip).
function triggerBlobDownload(blob, fname) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// Per-file state shape:
//   { id, file, look, intensity, previewUrl, previewLoading, converting }
let nextId = 1;

export default function CopyFaxConverterPage() {
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  // Per user request 2026-03-03: once any file is downloaded, show a
  // "Next → Mail Report?" CTA at the bottom of the page so the user can
  // continue the FTC flow without re-navigating.
  const [hasAnyDownload, setHasAnyDownload] = useState(false);
  const [showNextPrompt, setShowNextPrompt] = useState(false);
  // Per-file request-token map. Each new preview call bumps the token; only the
  // matching response is allowed to write back. This kills the race when the
  // user rapidly drags the tilt/distortion sliders and earlier in-flight
  // requests would otherwise overwrite a newer preview.
  const previewTokenRef = useRef({});

  const addFiles = useCallback((rawFiles) => {
    const incoming = Array.from(rawFiles).filter(f => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'));
    if (incoming.length === 0) {
      toast.error('Please drop PDF files only');
      return;
    }
    setFiles(prev => [
      ...prev,
      ...incoming.map(f => ({
        id: nextId++,
        file: f,
        look: 'copy',
        intensity: 5,
        tilt: 0,
        previewUrl: null,
        previewLoading: false,
        converting: false,
      })),
    ]);
    toast.success(`Added ${incoming.length} file${incoming.length === 1 ? '' : 's'}`);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handlePick = (e) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = '';
  };

  const updateFile = (id, patch) => {
    setFiles(prev => prev.map(f => (f.id === id ? { ...f, ...patch } : f)));
  };

  const removeFile = (id) => {
    setFiles(prev => {
      const target = prev.find(f => f.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter(f => f.id !== id);
    });
  };

  const generatePreview = async (entry) => {
    // Bump the per-file token so any in-flight response with a stale token is
    // discarded (prevents the slow first response from overwriting a newer one).
    const myToken = (previewTokenRef.current[entry.id] || 0) + 1;
    previewTokenRef.current[entry.id] = myToken;
    updateFile(entry.id, { previewLoading: true });
    try {
      const form = new FormData();
      form.append('file', entry.file);
      form.append('look', entry.look);
      form.append('intensity', String(entry.intensity));
      form.append('tilt', String(entry.tilt || 0));
      form.append('page', '0');
      const { data } = await axios.post(`${API}/api/copy-fax/preview`, form, {
        responseType: 'blob',
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000, // 3 min — large/multi-page PDFs can take a while on the server
      });
      // If a newer preview was kicked off while we were waiting, drop this one.
      if (previewTokenRef.current[entry.id] !== myToken) {
        return;
      }
      const url = URL.createObjectURL(data);
      // Revoke the old preview URL before swapping in the new one
      setFiles(prev => prev.map(f => {
        if (f.id !== entry.id) return f;
        if (f.previewUrl) {
          try { URL.revokeObjectURL(f.previewUrl); } catch (_) { /* ignore */ }
        }
        return { ...f, previewUrl: url, previewLoading: false };
      }));
    } catch (err) {
      if (previewTokenRef.current[entry.id] !== myToken) return; // stale failure, ignore
      // 2026-07-19: Only surface non-timeout errors — 504s and 413s are
      // expected on huge PDFs and shouldn't block the user from clicking
      // Convert (which uses the async job pipeline and works fine on
      // multi-MB files).  Just clear the spinner silently.
      const status = err?.response?.status;
      if (status === 504 || status === 413) {
        updateFile(entry.id, { previewLoading: false, previewSkipped: true });
        return;
      }
      console.error(err);
      const detail = await extractBlobError(err);
      toast.error('Preview failed: ' + detail);
      updateFile(entry.id, { previewLoading: false });
    }
  };

  const convertAndDownload = async (entry, { autoDownload = true } = {}) => {
    updateFile(entry.id, { converting: true });
    try {
      // ── Step 1: kick off the conversion job. Returns a job_id immediately so
      // this POST stays well under any edge-proxy (Cloudflare 520) timeout.
      const form = new FormData();
      form.append('file', entry.file);
      form.append('look', entry.look);
      form.append('intensity', String(entry.intensity));
      form.append('tilt', String(entry.tilt || 0));
      // Helper: post the upload with a single automatic retry on 520/502/504.
      // These are CDN-side transient errors that almost always succeed on a
      // second attempt; saves the user from re-clicking after a slow upload.
      const postKickoff = async () => {
        try {
          return await axios.post(`${API}/api/copy-fax/convert-async`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
          });
        } catch (err) {
          const status = err?.response?.status;
          if (status === 520 || status === 502 || status === 504) {
            // Brief pause then one retry. The CDN cooldown is usually a second or two.
            await new Promise(r => setTimeout(r, 1500));
            return await axios.post(`${API}/api/copy-fax/convert-async`, form, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 120000,
            });
          }
          throw err;
        }
      };
      const { data: kickoff } = await postKickoff();
      const jobId = kickoff?.job_id;
      if (!jobId) throw new Error('Server did not return a job id');

      // ── Step 2: poll until the job finishes. Each poll is a fast JSON GET, so
      // the connection never has to stay open for the full render.
      const startedAt = Date.now();
      const maxWaitMs = 10 * 60 * 1000; // 10 min absolute ceiling
      let pollDelay = 800;
      let serverFilename = kickoff?.filename;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (Date.now() - startedAt > maxWaitMs) {
          throw new Error('Conversion is taking too long — please try a smaller PDF.');
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise(r => setTimeout(r, pollDelay));
        pollDelay = Math.min(pollDelay + 200, 2500); // back off gently
        // eslint-disable-next-line no-await-in-loop
        const { data: status } = await axios.get(`${API}/api/copy-fax/job/${jobId}`, { timeout: 30000 });
        if (status?.filename) serverFilename = status.filename;
        if (status?.status === 'error') {
          throw new Error(status?.error || 'Server reported a conversion error');
        }
        if (status?.status === 'done') break;
      }

      // ── Step 3: download the finished PDF.
      const { data: blobData } = await axios.get(`${API}/api/copy-fax/job/${jobId}/download`, {
        responseType: 'blob',
        timeout: 180000,
      });
      const fname = serverFilename || `${entry.file.name.replace(/\.pdf$/i, '')}_${entry.look}.pdf`;
      const blob = new Blob([blobData], { type: 'application/pdf' });
      // Always stash the rendered blob + filename on the entry so the per-file
      // "Download" button below the card can re-trigger the save without a
      // re-conversion. When convertAll runs we skip the auto-download to avoid
      // browser pop-up blockers / throttling on rapid <a>.click() calls.
      updateFile(entry.id, {
        downloadedAt: new Date().toISOString(),
        downloadedBlob: blob,
        downloadedName: fname,
      });
      setHasAnyDownload(true);
      if (autoDownload) {
        triggerBlobDownload(blob, fname);
        toast.success(`✅ Downloaded ${fname}`);
      } else {
        toast.success(`✅ Converted ${fname}`);
      }
    } catch (err) {
      console.error(err);
      const status = err?.response?.status;
      // Friendlier message for the persistent 520/502/504 Cloudflare errors
      // — the upload made it through the CDN but the edge ran out of patience.
      if (status === 520 || status === 502 || status === 504) {
        toast.error(
          `Network hiccup (${status}) — the server is up but the connection got cut. Please try again in a few seconds. If it keeps happening, try a smaller PDF.`,
          { duration: 8000 }
        );
      } else {
        const detail = err?.response ? await extractBlobError(err) : (err?.message || 'Unknown error');
        toast.error('Conversion failed: ' + detail);
      }
    } finally {
      updateFile(entry.id, { converting: false });
    }
  };

  const convertAll = async () => {
    // 2026-06-05 fix: previously this looped convertAndDownload which fired
    // an auto-download <a>.click() per file — Chrome/Safari throttle rapid
    // sequential downloads and silently drop everything after the first one
    // or two. We now CONVERT all files first (no auto-download), then the
    // user clicks the individual "Save" buttons in the Ready-downloads panel
    // (which never gets throttled because each click is user-initiated).
    for (const entry of files) {
      // eslint-disable-next-line no-await-in-loop
      await convertAndDownload(entry, { autoDownload: false });
    }
    toast.success('All files converted. Click "Save" on each below to download.');
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 sm:p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" data-testid="copy-fax-back-home">
            <Button variant="outline" className="border-zinc-700 text-zinc-300">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Home
            </Button>
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2 text-white">
            <Printer className="w-7 h-7 text-blue-400" />
            Copy &amp; Fax Converter
          </h1>
          <div className="w-20" />
        </div>

        <p className="text-zinc-400 text-sm mb-6">
          Drop one or more PDFs below. Pick a look (📠 Fax or 📄 Copy), adjust the distortion notch from 1-10, and download a converted PDF that looks like it came out of a real scanner or office fax machine.
        </p>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          data-testid="copy-fax-dropzone"
          className={`border-2 border-dashed rounded-2xl p-10 mb-6 text-center transition-colors cursor-pointer ${
            dragOver ? 'border-blue-400 bg-blue-500/10' : 'border-zinc-700 bg-zinc-900/40 hover:bg-zinc-900/70'
          }`}
          onClick={() => document.getElementById('copy-fax-file-input')?.click()}
        >
          <UploadCloud className="w-12 h-12 mx-auto mb-3 text-blue-400" />
          <p className="text-zinc-200 font-medium text-base mb-1">Drop PDFs here or click to browse</p>
          <p className="text-zinc-500 text-xs">Multiple PDFs supported · per-file look &amp; intensity controls</p>
          <input
            id="copy-fax-file-input"
            type="file"
            accept="application/pdf"
            multiple
            onChange={handlePick}
            className="hidden"
            data-testid="copy-fax-file-input"
          />
        </div>

        {files.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mb-4">
            <Button onClick={convertAll} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="copy-fax-convert-all-btn">
              <Download className="w-4 h-4 mr-2" />
              Convert All ({files.length})
            </Button>
          </div>
        )}

        {/* Ready downloads panel — appears once any file has been converted.
            Each file has its own Save button so the browser never has to
            chain multiple <a>.click() calls (which Chrome/Safari throttle and
            silently drop). Also exposes a "Save all" that triggers them with
            a small stagger so the browser allows each one through. */}
        {files.some(f => f.downloadedBlob) && (
          <div className="bg-zinc-900 border border-emerald-700/40 rounded-xl p-4 mb-4" data-testid="copy-fax-ready-panel">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-emerald-300 font-semibold text-sm flex items-center gap-2">
                <Check className="w-4 h-4" />
                Ready to download ({files.filter(f => f.downloadedBlob).length})
              </h3>
              {files.filter(f => f.downloadedBlob).length > 1 && (
                <button
                  onClick={async () => {
                    // Stagger each download by ~350 ms so the browser's "burst
                    // download" guard releases each one. This is the
                    // browser-friendly equivalent of a zip without needing
                    // any extra dependency.
                    const ready = files.filter(f => f.downloadedBlob);
                    for (let i = 0; i < ready.length; i++) {
                      const f = ready[i];
                      triggerBlobDownload(f.downloadedBlob, f.downloadedName);
                      // eslint-disable-next-line no-await-in-loop
                      await new Promise(r => setTimeout(r, 350));
                    }
                    toast.success(`Saved ${ready.length} files`);
                  }}
                  className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded-md font-semibold"
                  data-testid="copy-fax-save-all-btn"
                >
                  <Download className="w-3 h-3 inline mr-1" />
                  Save all
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {files.filter(f => f.downloadedBlob).map(f => (
                <button
                  key={f.id}
                  onClick={() => {
                    triggerBlobDownload(f.downloadedBlob, f.downloadedName);
                    toast.success(`Saved ${f.downloadedName}`);
                  }}
                  className="flex items-center justify-between gap-2 bg-zinc-800 hover:bg-zinc-750 border border-zinc-700 rounded-lg px-3 py-2 text-left transition-colors"
                  data-testid={`copy-fax-ready-${f.id}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span className="text-zinc-100 text-xs truncate" title={f.downloadedName}>{f.downloadedName}</span>
                  </span>
                  <Download className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Per-file cards */}
        <div className="space-y-4">
          {files.map(entry => (
            <FileCard
              key={entry.id}
              entry={entry}
              onUpdate={(patch) => updateFile(entry.id, patch)}
              onPreview={() => generatePreview(entry)}
              onDownload={() => convertAndDownload(entry)}
              onRemove={() => removeFile(entry.id)}
            />
          ))}
        </div>

        {files.length === 0 && (
          <div className="text-center text-zinc-600 py-12">
            <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No PDFs queued yet.</p>
          </div>
        )}

        {/* Per user request 2026-03-03: once at least one file has been
            downloaded, show a sticky CTA that asks if they want to mail the
            converted docs.  Tapping Next → opens the prompt modal, which
            then either deep-links into the FTC flow's Mail Report drop zone
            (via sessionStorage hand-off) or simply lets the user close out. */}
        {hasAnyDownload && (
          <div className="sticky bottom-4 mt-6 z-30">
            <div className="bg-gradient-to-r from-emerald-600 to-cyan-600 border border-emerald-400/40 rounded-2xl p-4 shadow-2xl flex flex-col sm:flex-row items-center justify-between gap-3" data-testid="copy-fax-next-cta">
              <div className="text-left">
                <p className="text-white font-bold text-base">Documents ready ✅</p>
                <p className="text-emerald-100 text-[12px]">Want to mail them off through Lob now?</p>
              </div>
              <button
                onClick={() => setShowNextPrompt(true)}
                className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold px-5 py-2.5 rounded-lg shadow-md flex items-center gap-2 transition-colors"
                data-testid="copy-fax-next-btn"
              >
                Next
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Mail-off prompt modal */}
        {showNextPrompt && (
          <div className="fixed inset-0 z-[150] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3" data-testid="copy-fax-next-modal">
            <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-5 py-4 flex items-center gap-2">
                <Send className="w-5 h-5 text-white" />
                <h3 className="text-white font-bold text-base">Mail these documents off?</h3>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-zinc-300 text-sm">
                  You've downloaded {files.filter(f => f.downloadedAt).length} converted file{files.filter(f => f.downloadedAt).length === 1 ? '' : 's'}.
                  Would you like to mail them through Lob.com now?
                </p>
                <ul className="text-[12px] text-zinc-400 space-y-1 pl-4 list-disc">
                  <li>A blank cover page is auto-added before page 1 (so Lob's address overlay never collides with content)</li>
                  <li>You can mail to one or all 3 bureaus in a single send</li>
                  <li>If you came from the FTC report flow, just close this tab and the Mail Report popup is already waiting on the previous tab</li>
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-2 p-4 border-t border-zinc-700 bg-zinc-900/60">
                <button
                  onClick={() => setShowNextPrompt(false)}
                  className="px-4 py-2.5 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white text-sm font-semibold"
                  data-testid="copy-fax-next-modal-skip"
                >
                  No, I'll handle it later
                </button>
                <button
                  onClick={() => {
                    // Hand-off to the FTC flow's Mail Report popup.  We set a
                    // sessionStorage flag the FTC builder picks up when it
                    // remounts so the popup auto-opens if there's an active
                    // report context; otherwise we send the user to the FTC
                    // builder home and let them pick a report.
                    try { sessionStorage.setItem('pendingMailReport', '1'); } catch (_) { /* ignore */ }
                    setShowNextPrompt(false);
                    window.location.href = '/ftc-builder';
                  }}
                  className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-1.5"
                  data-testid="copy-fax-next-modal-mail"
                >
                  <Send className="w-4 h-4" />
                  Yes, mail them
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function FileCard({ entry, onUpdate, onPreview, onDownload, onRemove }) {
  const { file, look, intensity, previewUrl, previewLoading, converting } = entry;

  // Live preview: once a preview has been generated for this file, auto-refresh
  // it whenever look / intensity / tilt change (debounced 450 ms so we don't
  // hammer the server on every slider tick). We keep the OLD preview image
  // visible while the new one renders so the user sees a smooth transition.
  const previewActive = !!previewUrl;
  const onPreviewRef = useRef(onPreview);
  useEffect(() => { onPreviewRef.current = onPreview; }, [onPreview]);
  // Skip the very first render (don't auto-trigger before user clicks Preview).
  const skipFirstRef = useRef(true);
  const tiltValue = entry.tilt;
  useEffect(() => {
    if (skipFirstRef.current) { skipFirstRef.current = false; return; }
    if (!previewActive) return;
    const t = setTimeout(() => {
      if (onPreviewRef.current) onPreviewRef.current();
    }, 450);
    return () => clearTimeout(t);
  }, [look, intensity, tiltValue, previewActive]);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`copy-fax-file-card-${entry.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2 min-w-0">
          <FileText className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold truncate" title={file.name}>{file.name}</p>
            <p className="text-zinc-500 text-[11px]">{(file.size / 1024).toFixed(1)} KB</p>
          </div>
        </div>
        <button onClick={onRemove} className="text-zinc-500 hover:text-red-400 transition-colors" data-testid={`copy-fax-remove-${entry.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Look toggle */}
        <div>
          <label className="block text-zinc-400 text-xs font-medium mb-1.5 uppercase tracking-wide">Look</label>
          <div className="flex bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
            <button
              onClick={() => onUpdate({ look: 'fax' })}
              data-testid={`copy-fax-look-fax-${entry.id}`}
              className={`flex-1 py-2 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                look === 'fax' ? 'bg-blue-600 text-white' : 'text-zinc-400 hover:text-white'
              }`}
            >
              <Send className="w-3.5 h-3.5" />
              Fax (B&amp;W)
            </button>
            <button
              onClick={() => onUpdate({ look: 'copy' })}
              data-testid={`copy-fax-look-copy-${entry.id}`}
              className={`flex-1 py-2 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${
                look === 'copy' ? 'bg-amber-600 text-white' : 'text-zinc-400 hover:text-white'
              }`}
            >
              <Printer className="w-3.5 h-3.5" />
              Copy (grayscale)
            </button>
          </div>
        </div>

        {/* Intensity slider */}
        <div>
          <label className="block text-zinc-400 text-xs font-medium mb-1.5 uppercase tracking-wide">
            Distortion <span className="text-blue-300 font-mono">{intensity}</span> / 10
            {previewActive && (
              <span className="ml-2 text-[10px] text-emerald-400/80 normal-case tracking-normal">· live</span>
            )}
          </label>
          <Slider
            value={[intensity]}
            min={1}
            max={10}
            step={1}
            onValueChange={(v) => onUpdate({ intensity: v[0] })}
            data-testid={`copy-fax-intensity-${entry.id}`}
          />
          <p className="text-[10px] text-zinc-500 mt-1">1 = clean & clearly readable · 10 = "2003 office fax"</p>
        </div>
      </div>

      {/* Tilt slider (full width — independent of intensity) */}
      <div className="mt-4">
        <label className="block text-zinc-400 text-xs font-medium mb-1.5 uppercase tracking-wide flex items-center justify-between">
          <span>
            Page tilt <span className="text-amber-300 font-mono">
              {(entry.tilt || 0) > 0 ? `+${(entry.tilt || 0).toFixed(1)}°` : `${(entry.tilt || 0).toFixed(1)}°`}
            </span>
            {previewActive && (
              <span className="ml-2 text-[10px] text-emerald-400/80 normal-case tracking-normal">· live</span>
            )}
          </span>
          <button
            type="button"
            onClick={() => onUpdate({ tilt: 0 })}
            className="text-[10px] text-zinc-500 hover:text-amber-300 underline"
            title="Reset tilt to 0°"
          >
            reset
          </button>
        </label>
        <Slider
          value={[entry.tilt || 0]}
          min={-10}
          max={10}
          step={0.5}
          onValueChange={(v) => onUpdate({ tilt: v[0] })}
          data-testid={`copy-fax-tilt-${entry.id}`}
        />
        <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
          <span>-10° (heavy left tilt)</span>
          <span>0° (straight)</span>
          <span>+10° (heavy right tilt)</span>
        </div>
      </div>

      {/* Preview — keeps showing the old image while the new one renders */}
      {previewUrl && (
        <div className="mt-4 relative bg-zinc-950 border border-zinc-800 rounded-lg p-2 max-h-[520px] overflow-auto">
          <img
            src={previewUrl}
            alt="Page 1 preview"
            className={`w-full h-auto transition-opacity duration-200 ${previewLoading ? 'opacity-60' : 'opacity-100'}`}
            data-testid={`copy-fax-preview-img-${entry.id}`}
          />
          {previewLoading && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-zinc-950/40 backdrop-blur-[1px]">
              <div className="flex items-center gap-2 bg-zinc-900/90 border border-zinc-700 rounded-full px-3 py-1.5 shadow-lg">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />
                <span className="text-[11px] text-zinc-200 font-medium">Re-rendering…</span>
              </div>
            </div>
          )}
          <button
            onClick={() => onUpdate({ previewUrl: null })}
            className="absolute top-2 right-2 text-zinc-400 hover:text-white bg-zinc-900/80 border border-zinc-700 rounded p-1"
            title="Close preview"
            data-testid={`copy-fax-preview-close-${entry.id}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="flex gap-2 mt-4">
        <Button
          onClick={onPreview}
          disabled={previewLoading}
          variant="outline"
          className="flex-1 border-zinc-700 text-zinc-200 hover:bg-zinc-800"
          data-testid={`copy-fax-preview-btn-${entry.id}`}
        >
          {previewLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
          {previewUrl ? 'Refresh preview' : 'Preview page 1'}
        </Button>
        <Button
          onClick={onDownload}
          disabled={converting}
          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid={`copy-fax-download-btn-${entry.id}`}
        >
          {converting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
          Complete &amp; Download
        </Button>
      </div>
    </div>
  );
}
